import { useEffect, useRef } from 'react'
import * as d3 from 'd3'
import { max, min, axisBottom, select, selectAll } from 'd3'
import { scaleLinear, scaleLog } from 'd3-scale'
import './beeswarm.css'
import { toJpeg, toPng, toSvg } from 'html-to-image'
import CorrelationLegend from '../CorrelationLegend'
import { COLOR_MAP } from '../../../../common/utils/theme'
import { useState } from 'react'
import { useReactToPrint } from 'react-to-print'
import '../../common/fullScreen.css'
import useCloseOnOutsideClick from '../../../../common/hooks/useCloseOnOutsideClick'
import {
  AxisValues,
  ChartElements,
  disaggregations,
} from '../../../../common/utils/enum'
import {
  formatNumberOnUnitMeasure,
  formatUnitMeasure,
  formatUnitMult,
  getUnitMeasureItems,
} from '../../../../common/utils/helper'
import {
  getColorForCorrelation,
  getMinorGridlinesForLogAxis,
} from '../../common/helper'
import NoDataImage from '../../../../common/components/NoDataImage'

const Beeswarm = ({
  beeswarmData,
  indicatorCode,
  unitMeasure,
  unitMeasureName,
  noDataImageURL,
  disaggregationData,
  unitMeasureList,
  site,
  indicatorSpecificRegions,
  selectedOnlyShow,
}) => {
  const { data, title, subtitle, badges, aggregateData, indicatorMetadata } =
    beeswarmData
  const beeswarmRef = useRef()
  var tooltip

  const [showMenu, setShowMenu] = useState(false)
  const [isFullScreenMode, setIsFullScreenMode] = useState(false)
  const beeswarmDownloadMenuRef = useRef(null)

  useCloseOnOutsideClick(beeswarmDownloadMenuRef, () => setShowMenu(false))

  useEffect(() => {
    if (beeswarmRef?.current && (data?.length || aggregateData?.length)) {
      buildBeeswarm()
    }
  }, [beeswarmRef?.current, data, aggregateData])

  useEffect(() => {
    const handleResize = () => {
      buildBeeswarm()
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  // Filter out null values
  let filteredData = data?.filter((d) => d.value !== null)
  const getTooltipContent = (d) => {
    return `<div class="tooltip-top"> <span class="tooltip-country">${
      d.country
    }, </span><span class="tooltip-year"> ${d.year}</span></div> 
    <div class="tooltip-bottom"><span class="tooltip-value">${formatNumberOnUnitMeasure(
      {
        value: d.value,
        unitMeasure:
          unitMeasure || filteredData?.[0]?.[disaggregations.UnitMeasure],
        decimals: filteredData?.[0]?.[disaggregations.Decimals],
      }
    )} ${formatUnitMult(
      disaggregationData,
      filteredData?.[0]?.[disaggregations.UnitMult]
    )} ${formatUnitMeasure(
      unitMeasure || filteredData?.[0]?.[disaggregations.UnitMeasure],
      unitMeasureName ||
        unitMeasureList.find(
          (item) =>
            item.code === filteredData?.[0]?.[disaggregations.UnitMeasure]
        )?.description ||
        '',
      getUnitMeasureItems(disaggregationData)?.find(
        (item) => item.code === unitMeasure
      )?.short_label ||
        unitMeasureList.find(
          (item) =>
            item.code === filteredData?.[0]?.[disaggregations.UnitMeasure]
        )?.short_label ||
        ''
    )}</span></div>`
  }

  const buildBeeswarm = () => {
    // Set up chart dimensions
    const margin = { top: 60, right: 30, bottom: 50, left: 20 }
    const width =
      document?.querySelector?.('.tui__chart_content')?.clientWidth -
      margin.left -
      margin.right
    const height = 450 - margin.top - margin.bottom

    select(beeswarmRef.current).select('svg').remove()

    // Append SVG to the chart container
    const svg = select(beeswarmRef.current)
      .append('svg')
      .attr('width', width + margin.left + margin.right)
      // .attr('height', height + margin.top + margin.bottom)
      .attr('height', height + margin.top)
      .append('g')
      // .attr('transform', `translate(${margin.left},${margin.top})`)
      .attr('transform', `translate(${margin.left},20)`)

    // Create scales for x and y axes
    let xScale = ''
    // .padding(0.1)

    let axisValues =
      indicatorMetadata?.admin_metadata?.[site]?.visualization?.axis_values ||
      ''
    let axisValuesParams =
      indicatorMetadata?.admin_metadata?.[site]?.visualization
        ?.axis_values_params || ''

    if (
      axisValues === AxisValues.Logarithmic ||
      axisValues === AxisValues.Exponential
    ) {
      // log: Natural log scale
      // exponential: base 10 log scale(default), base n log scale (n given in axis_values_params)
      let updatedData = filteredData.map((d) => d.value)
      if (aggregateData && aggregateData.length > 0) {
        updatedData = [...updatedData, aggregateData.map((item) => item.value)]
      }

      // TO-DO: hope d<=0 condition need not be handled as log scale might not be applicable to 0 or negative numbers.
      // .map((d) => (d <= 0 ? 1e-2 : d))
      // filteredData = filteredData.map((item) => {
      //   return { ...item, value: item.value <= 0 ? 1e-2 : item.value }
      // })
      if (axisValues === AxisValues.Logarithmic) {
        xScale = scaleLog()
          .domain([min(updatedData), max(updatedData)])
          .range([margin.left, width])
          .base(Math.E)
          .nice()
      } else {
        if (!axisValuesParams || axisValuesParams === '10') {
          xScale = scaleLog()
            .domain([min(updatedData), max(updatedData)])
            .range([margin.left, width])
            .nice()
        } else if (axisValuesParams) {
          xScale = scaleLog()
            .domain([min(updatedData), max(updatedData)])
            .range([margin.left, width])
            .base(Number(axisValuesParams))
            .nice()
        }
      }
    } else {
      let updatedData = filteredData.map((d) => d.value)
      if (aggregateData && aggregateData.length > 0) {
        updatedData = [
          ...updatedData,
          ...aggregateData.map((item) => item.value),
        ]
      }
      const minValue = min(updatedData),
        maxValue = max(updatedData)
      xScale = scaleLinear()
        .domain([minValue, maxValue])
        .range([margin.left, width])
      if (
        indicatorMetadata?.admin_metadata?.[site]?.visualization?.axis_type ===
          'fix' &&
        indicatorMetadata?.admin_metadata?.[site]?.visualization
          ?.axis_type_params?.[0]?.abs_min &&
        indicatorMetadata?.admin_metadata?.[site]?.visualization
          ?.axis_type_params?.[0]?.abs_max
      ) {
        const min = Number(
          indicatorMetadata?.admin_metadata?.[site]?.visualization
            ?.axis_type_params?.[0]?.abs_min
        )
        const max = Number(
          indicatorMetadata?.admin_metadata?.[site]?.visualization
            ?.axis_type_params?.[0]?.abs_max
        )
        xScale = scaleLinear().domain([min, max]).range([margin.left, width])
      } else {
        let dataMin = min(updatedData)
        let dataMax = max(updatedData)
        if (
          indicatorMetadata?.admin_metadata?.[site]?.visualization
            ?.axis_type_params?.[0]?.abs_min
        ) {
          dataMin = Math.max(
            dataMin,
            Number(
              indicatorMetadata?.admin_metadata?.[site]?.visualization
                ?.axis_type_params?.[0]?.abs_min
            )
          )
        }
        if (
          indicatorMetadata?.admin_metadata?.[site]?.visualization
            ?.axis_type_params?.[0]?.abs_max
        ) {
          dataMax = Math.min(
            dataMax,
            Number(
              indicatorMetadata?.admin_metadata?.[site]?.visualization
                ?.axis_type_params?.[0]?.abs_max
            )
          )
        }
        xScale = scaleLinear()
          .domain([dataMin, dataMax])
          .range([margin.left, width])
      }
    }

    // Append axes to the SVG
    let axis = axisBottom(xScale)
      // .ticks(7)
      .tickFormat((d) =>
       formatNumberOnUnitMeasure({
          value: d,
          unitMeasure:
            unitMeasure || filteredData?.[0]?.[disaggregations.UnitMeasure],
          decimals: filteredData?.[0]?.[disaggregations.Decimals],
        })
      )
    // show lesser ticks when device width is smaller
    if (
      axisValues !== AxisValues.Logarithmic &&
      axisValues !== AxisValues.Exponential
    ) {
      if (window.innerWidth < 550) {
        axis = axisBottom(xScale)
          .ticks(5)
          .tickFormat((d) =>
            formatNumberOnUnitMeasure({
              value: d,
              unitMeasure:
                unitMeasure || filteredData?.[0]?.[disaggregations.UnitMeasure],
              decimals: filteredData?.[0]?.[disaggregations.Decimals],
            })
          )
      }
    }
    const xAxis = svg
      .append('g')
      .attr('transform', `translate(0, ${height - 20})`)
      .call(axis)
    xAxis.select('.domain').attr('stroke', COLOR_MAP[ChartElements.ZeroLine])
    xAxis.selectAll('.tick line').attr('stroke', (d) => {
      let color = COLOR_MAP[ChartElements.ZeroLine]
      // hide minor tick lines in log scales
      color = getColorForCorrelation(
        d,
        axisValues,
        axisValuesParams,
        COLOR_MAP[ChartElements.ZeroLine],
        ''
      )
      return color
    })
    xAxis
      .selectAll('.tick text')
      .attr('fill', (d) => {
        let color = 'rgba(0, 0, 0, 0.6)'
        // hide minor tick numbers in log scale
        color = getColorForCorrelation(
          d,
          axisValues,
          axisValuesParams,
          'rgba(0, 0, 0, 0.6)',
          ''
        )
        return color
      })
      .style('font-size', '14px')

    let text = svg
      .append('text')
      .attr(
        'transform',
        `translate(${(width + margin.left + margin.right) / 2},${height + 20})`
        // `translate(0,${height + 20})`
      )
      .style('text-anchor', 'middle')
      .text(title)
      // .text(
      //   'Adolescent suicide rate (per 100,000 population), ages 15-19 Adolescent suicide rate (per 100,000 population), ages 15-19'
      // )
      .attr('fill', 'black')
      .style('font-weight', 700)
      .style('font-family', '"Open Sans", sans-serif')
      .style('font-size', '14px')
      .call(wrap, width)

    let ticks = xScale.ticks()

    if (axisValues === AxisValues.Logarithmic) {
      ticks = getMinorGridlinesForLogAxis(ticks)
    }
    // Append vertical grid lines
    svg
      .append('g')
      .attr('class', 'grid')
      .selectAll('line')
      .data(ticks)
      .enter()
      .append('line')
      .attr('x1', (d) => xScale(d))
      .attr('x2', (d) => xScale(d))
      .attr('y1', 0)
      .attr('y2', height - 20)
    selectAll('.grid line').attr('stroke', (d) => {
      let color =
        d === 0
          ? COLOR_MAP[ChartElements.ZeroLine]
          : COLOR_MAP[ChartElements.GridLine]
      color = getColorForCorrelation(
        d,
        axisValues,
        axisValuesParams,
        COLOR_MAP[ChartElements.GridLine],
        COLOR_MAP[ChartElements.MinorGridLineLogAxis]
      )
      return color
    })
    if (aggregateData.length > 0) {
      aggregateData.forEach((item, index) => {
        if (
          item.value !== 'null' &&
          item.value !== 'NaN' &&
          item.value !== null &&
          item.value !== undefined
        ) {
          svg
            .append('line')
            .attr('class', 'line')
            .attr('x1', xScale(parseFloat(item.value)))
            .attr('x2', xScale(parseFloat(item.value)))
            .attr('y1', 0)
            .attr('y2', height)
            .attr('stroke', COLOR_MAP.benchmark)
            .attr('stroke-width', 1.05)
            .attr('stroke-dasharray', '5 2')
        }
      })
    }

    // Create beeswarm points
    svg
      .selectAll('.beeswarm')
      .data(filteredData)
      .enter()
      .append('circle')
      .attr('class', 'beeswarm')
      .attr('cx', (d) => {
        return xScale(d?.value)
      })
      .attr('cy', height / 2)

      .attr('r', 5)
      .attr('fill', (d) => d.color)
      .style('opacity', (d) => 1)
      .style('stroke', (d) =>
        d.selectedCountry ? COLOR_MAP['highlight'] : undefined
      )
      .style('stroke-width', (d) => (d.selectedCountry ? '3' : undefined))

      .on('mouseover', function (d) {
        select('body').selectAll('div.beeswarm-tooltip').remove()
        tooltip = select(beeswarmRef.current)
          .append('div')
          .attr('class', 'beeswarm-tooltip')
          .html(getTooltipContent(d))
          .style('visibility', 'visible')
          .style('left', d3.mouse(this)[0] - 20 + 'px')
          .style('top', d3.mouse(this)[1] + 25 + 'px')
      })
      .on('mouseleave', function (d) {
        tooltip.style('visibility', 'hidden')
      })

    if (aggregateData.length > 0) {
      // const iconHeight = 18
      // const minSpacing = 4
      const threshold =
        (max(filteredData.map((d) => d.value)) -
          min(filteredData.map((d) => d.value))) *
        0.02
      const sortedAggregateData = aggregateData.sort((a, b) => {
        return (parseFloat(a.value) || 0) - (parseFloat(b.value) || 0)
      })
      let lastLabelOffset = 0
      for (let i = 1; i < sortedAggregateData.length; i++) {
        let labelOffset = 0
        const prev = sortedAggregateData[i - 1]
        const curr = sortedAggregateData[i]

        if (Math.abs(curr.value - prev.value) < threshold) {
          lastLabelOffset -= -20
          labelOffset = lastLabelOffset
        } else {
          lastLabelOffset = -20
          labelOffset = lastLabelOffset
        }
        curr._adjustedY = labelOffset
      }

      aggregateData.forEach((item, index) => {
        if (
          item.value !== 'null' &&
          item.value !== 'NaN' &&
          item.value !== null &&
          item.value !== undefined
        ) {
          svg
            .append('foreignObject')
            .attr('class', 'info-icon')
            .attr('x', xScale(parseFloat(item.value)) - 10)
            .attr('y', item._adjustedY !== undefined ? item._adjustedY : -20)
            .attr('width', 18)
            .attr('height', 18)
            .append('xhtml:div.plotline-info-icon')
            .html(
              '<i class="lp lp-info-circle-solid" style="position:fixed;z-index:1000;"></i>'
            )
            .style('cursor', 'pointer')
            .on('click', function () {
              let icon = this
              if (item.label) {
                document
                  .querySelectorAll('.plotline-tooltip')
                  .forEach((tooltip) => {
                    tooltip.remove()
                  })
                const tooltip = document.createElement('div')
                tooltip.innerText = item.label
                tooltip.style.display = 'block'
                tooltip.style.position = 'fixed'
                tooltip.style.background = '#ffffff'
                tooltip.style.marginRight = '20px'
                tooltip.style.border = '1px solid #ccc'
                tooltip.style.padding = '4px 8px'
                tooltip.style.boxShadow = 'rgba(0, 0, 0, 0.15) 4px 4px 4px 0px'
                tooltip.style.fontSize = '14px'
                tooltip.style.zIndex = 0
                tooltip.className = 'plotline-tooltip tui_chart_tooltip_title'

                const rect = this.getBoundingClientRect()
                tooltip.style.left = rect.right + 8 + 'px'
                tooltip.style.top = rect.top + 'px'
                document.body.appendChild(tooltip)

                document.addEventListener('click', function removeTooltip(ev) {
                  if (
                    !tooltip.contains(ev.target) &&
                    !icon.contains(ev.target)
                  ) {
                    tooltip.remove()
                    document.removeEventListener('click', removeTooltip)
                  }
                })
              }
            })
        }
      })
    }
    // Logic to avoid collide
    const tick = () => {
      selectAll('.beeswarm').attr('cy', (d) => {
        return d.y
      })
    }

    let simulation = d3
      .forceSimulation(filteredData)
      .force(
        'x',
        d3
          .forceX((d) => {
            return xScale(d.value)
          })
          .strength(1)
      )
      .force('y', d3.forceY(height / 2).strength(0.2))
      .force('collide', d3.forceCollide(5 + 2))
      .alpha(0.5)
      .alphaDecay(0.01)
      .on('tick', () => {
        selectAll('.beeswarm')
          .attr('cx', (d) => d.x)
          .attr('cy', (d) => d.y)
      })

    let strength = simulation.force('collide').strength(),
      endTime = 1000
    let transitionTimer = d3.timer((elapsed) => {
      let dt = elapsed / endTime
      simulation.force('collide').strength(Math.pow(dt, 4) * strength)
      if (dt >= 1.0) transitionTimer.stop()
    })

    setTimeout(function () {
      simulation.alphaDecay(0.1)
    }, 500)
  }

  function wrap(text, width) {
    text.each(function () {
      const text = select(this)
      const title = text.text()
      const words = text.text().split(/\s+/).reverse()
      let word
      let line = []
      let y = text.attr('y')
      const dy = parseFloat(text.attr('dy') || 0)
      let tspan = text
        .text(null)
        .append('tspan')
        .attr('x', text.attr('x'))
        .attr('y', y)
        .attr('dy', dy + 'em')
        .style('text-anchor', 'middle')

      while ((word = words.pop())) {
        line.push(word)
        tspan.text(line.join(' '))
        if (tspan.node().getComputedTextLength() >= width - 70) {
          // wrapping with ellipsis if text is larger than chart width
          line.push('...')
          tspan.text(line.join(' '))
          // text.append('title').text(title)
          break
        }
      }
    })
  }

  // wrap benchmark text to multi line based on space available
  function wrapBenchmarkText(text, textWidth) {
    text.each(function () {
      const text = d3.select(this)
      const words = text.text().split(/\s+/).reverse()
      let word
      let line = []
      let lineNumber = 0
      const y = text.attr('y')
      const dy = parseFloat(text.attr('dy') || 0)
      let tspan = text
        .text(null)
        .append('tspan')
        .attr('x', text.attr('x'))
        .attr('y', y)
        .attr('dy', dy + 'em')
        .style('text-anchor', 'right')

      while ((word = words.pop())) {
        line.push(word)
        tspan.text(line.join(' '))
        if (tspan.node().getComputedTextLength() > textWidth) {
          line.pop()
          tspan.text(line.join(' '))
          line = [word]
          tspan = text
            .append('tspan')
            .attr('x', text.attr('x'))
            .attr('y', y)
            .attr('dx', '5px')
            .attr('dy', ++lineNumber * 1.1 + dy + 'em')
            .text(word)
        }
      }
    })
  }

  // Download as png
  const exportAsImage = (fileName, type) => {
    const node = document.getElementById('beeswarm_container')

    if (type === 'png') {
      toPng(node, { filter: excludeElements, backgroundColor: '#ffffff' })
        .then((dataUrl) => {
          const link = document.createElement('a')
          link.download = fileName
          link.href = dataUrl
          link.click()
        })
        .catch((err) => {
          console.log(err)
        })
    } else if (type === 'jpeg') {
      toJpeg(node, { filter: excludeElements, backgroundColor: '#ffffff' })
        .then((dataUrl) => {
          const link = document.createElement('a')
          link.download = fileName
          link.href = dataUrl
          link.click()
        })
        .catch((err) => {
          console.log(err)
        })
    } else if (type === 'svg') {
      toSvg(node, { filter: excludeElements, backgroundColor: '#ffffff' })
        .then((dataUrl) => {
          const link = document.createElement('a')
          link.download = fileName
          link.href = dataUrl
          link.click()
        })
        .catch((err) => {
          console.log(err)
        })
    }
  }

  const excludeElements = (node) => {
    const exclusionClasses = ['download-exclude']
    return !exclusionClasses.some((classname) =>
      node.classList?.contains(classname)
    )
  }

  const handleFullscreen = () => {
    if (!document.fullscreenElement) {
      enterFullscreen()
    } else {
      exitFullscreen()
    }
  }

   useEffect(() => {
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) {
        setIsFullScreenMode(false)
      }
    }
    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange)
  }, [])

  const enterFullscreen = () => {
    const elem = document.getElementById('beeswarm_container')
    if (elem.requestFullscreen) {
      elem.requestFullscreen()
    } else if (elem.mozRequestFullScreen) {
      // Firefox
      elem.mozRequestFullScreen()
    } else if (elem.webkitRequestFullscreen) {
      // Chrome, Safari, and Opera
      elem.webkitRequestFullscreen()
    }
     setIsFullScreenMode(true)
  }

  const exitFullscreen = () => {
    if (document.exitFullscreen) {
      document.exitFullscreen()
    } else if (document.mozCancelFullScreen) {
      // Firefox
      document.mozCancelFullScreen()
    } else if (document.webkitExitFullscreen) {
      // Chrome, Safari, and Opera
      document.webkitExitFullscreen()
    }
  }

  const handlePrint = useReactToPrint({
    content: () => document.getElementById('beeswarm_container'),
  })

  const badgesDiv =
    badges &&
    badges.length > 0 &&
    badges.map((badge) => (
      <div key={badge} className="text tui_badge">
        <div>
          <p>{badge}</p>
        </div>
      </div>
    ))

  const noData = !filteredData?.length && !aggregateData?.length

  return (
    <div id="beeswarm_container">
      <div
        className="tui_comparison_indicator_container tui_chart_dropdown"
        id="beeswarm_title_section"
      >
        <div className="tui_comparison_title_section">
          <div className="tui_heading_h2_sm">
            <h2>{title}</h2>
          </div>
          <p className="tui__text_base">{subtitle}</p>

          <div className="tui__badge_container">
            <div className="tui_badge_group">{badgesDiv}</div>
          </div>
        </div>
        {!isCustomIndicator && !noData && (
          <div
            className="button-container download-exclude"
            ref={beeswarmDownloadMenuRef}
          >
            <div
              className="download-icon"
              onClick={() => setShowMenu((prevState) => !prevState)}
              role="button"
              tabIndex="0"
              aria-haspopup="true"
              aria-expanded={showMenu ? 'true' : 'false'}
              id="beeswarmDownloadMenu"
            >
              <span className="lp lp-bars"></span>
            </div>
            <div
              className={`download-options download-exclude ${
                showMenu ? 'show' : 'hide'
              }`}
              aria-labelledby="beeswarmDownloadMenu"
            >
              <ul>
                <li
                  onClick={() => {
                    handleFullscreen()
                    setShowMenu(false)
                  }}
                >
                  {isFullScreenMode
                    ? 'Exit from full screen'
                    : 'View in full screen'}
                </li>
                <li
                  onClick={() => {
                    {
                      const fileName = `${indicatorCode}_beeswarm.png`
                      exportAsImage(fileName, 'png')
                      setShowMenu(false)
                    }
                  }}
                >
                  Download PNG image
                </li>
                <li
                  onClick={() => {
                    {
                      const fileName = `${indicatorCode}_beeswarm.jpg`
                      exportAsImage(fileName, 'jpeg')
                      setShowMenu(false)
                    }
                  }}
                >
                  Download JPEG image
                </li>
                <li
                  onClick={() => {
                    const fileName = `${indicatorCode}_beeswarm.svg`
                    exportAsImage(fileName, 'svg')
                    setShowMenu(false)
                  }}
                >
                  Download SVG
                </li>
                <li
                  onClick={() => {
                    handlePrint()
                    setShowMenu(false)
                  }}
                >
                  Print
                </li>
              </ul>
            </div>
            {/* )} */}
          </div>
        )}
      </div>
      <div className="tui__chart_content">
        {!noData ? (
          <>
            <div ref={beeswarmRef}></div>
            <CorrelationLegend
              indicatorSpecificRegions={indicatorSpecificRegions}
              selectedOnlyShow={selectedOnlyShow}
            />
          </>
        ) : (
          <NoDataImage imageURL={noDataImageURL} height="345px" width="100%" />
        )}
      </div>
    </div>
  )
}

export default Beeswarm
